import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { topicReducer } from "../../../state/reducers";
import { ITopic } from "../../../utils/types/models";

const TopicEditor: React.FC<IAddEntityScreenProps<ITopic> | IEditEntityScreenProps<ITopic>> = (props: IAddEntityScreenProps<ITopic> | IEditEntityScreenProps<ITopic>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(topicReducer, getInitialState<ITopic>());
	const initialTopic: ITopic = {
		id: (props as IEditEntityScreenProps<ITopic>).recordId || 0,
		name: "",
		active: true
	};
	const [topic, setTopic] = useState<ITopic>(initialTopic);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setTopic(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setTopic(handleChange(topic, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(topic.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: ITopic): void {
		if (record && !close) {
			setTopic(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<ITopic>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={topic}
			endpoint={Endpoint.Topics}
			entityState={state}
			entityType={t("topic")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={topic.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={topic.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={topic.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default TopicEditor;
